import { CommonClientError } from "../../error";
import { DateUtils, NumberUtils } from "../../primitive-utils";
export const DurationRegex = /^\d+:([0-5][0-9])(:([0-5][0-9]))?$/;
export const NumberSystems = ["Base60", "Base10", "Minutes"];
export const NumberSystemDisplay = {
    Base60: "60進数（1時間30分を01:30と表示）",
    Base10: "10進数（1時間30分を1.5と表示）※端数は四捨五入されます",
    Minutes: "分単位（1時間30分を90と表示）",
};
export class DurationFunc {
    seconds;
    constructor(seconds) {
        this.seconds = seconds;
    }
    static ZERO = DurationFunc.of({});
    static of(v) {
        const hours = (v.hours ?? 0) * 3600;
        const minutes = (v.minutes ?? 0) * 60;
        const seconds = v.seconds ?? 0;
        return new DurationFunc(hours + minutes + seconds);
    }
    static between({ startAt, endAt, }) {
        const seconds = DateUtils.differenceInSeconds(endAt.toDate(), startAt.toDate());
        return new DurationFunc(seconds);
    }
    // HH:mm or HH:mm:SS 形式からの変換
    static parse(text) {
        if (!DurationRegex.test(text)) {
            throw new CommonClientError.InvalidInput(`parse error. requierd: "HH:mm" or "HH:mm:SS" but actual:${text}`);
        }
        const regex = /\d+/g; // 数字を抽出
        const [hours, minutes, seconds] = text.match(regex)?.map(Number) ?? [];
        return DurationFunc.of({ hours, minutes, seconds });
    }
    static sum(array, conveter) {
        return array.reduce((sum, item) => sum.plus(conveter?.(item) ?? item), DurationFunc.ZERO);
    }
    static max(...durations) {
        const max = Math.max(...durations.map((d) => d.toSeconds()));
        return DurationFunc.of({ seconds: max });
    }
    static min(...durations) {
        const min = Math.min(...durations.map((d) => d.toSeconds()));
        return DurationFunc.of({ seconds: min });
    }
    floor(precision) {
        switch (precision) {
            case "Minutes":
                return DurationFunc.of({ ...this.toValues(), seconds: 0 });
        }
    }
    minus(other) {
        return new DurationFunc(this.seconds - other.seconds);
    }
    plus(other) {
        return new DurationFunc(this.seconds + other.seconds);
    }
    divide(n) {
        return new DurationFunc(Math.round(this.seconds / n));
    }
    compare(other) {
        return this.seconds - other.seconds;
    }
    isMoreThan(other) {
        return this.compare(other) > 0;
    }
    isMoreThanOrEqual(other) {
        return this.compare(other) >= 0;
    }
    isEqual(other) {
        return this.compare(other) === 0;
    }
    isLessThan(other) {
        return this.compare(other) < 0;
    }
    isLessThanOrEqual(other) {
        return this.compare(other) <= 0;
    }
    // 負の値か否か
    isNegative() {
        return this.seconds < 0;
    }
    toValues() {
        const hours = Math.floor(this.seconds / 3600);
        const minutes = Math.floor((this.seconds % 3600) / 60);
        const seconds = Math.round(this.seconds % 60);
        return {
            hours,
            minutes,
            seconds,
        };
    }
    toMinute() {
        return Math.floor(this.seconds / 60);
    }
    toSeconds() {
        return this.seconds;
    }
    convertTo(numberSystem = "Minutes") {
        switch (numberSystem) {
            case "Minutes": // １時間30分 -> ９０
                return this.toMinute();
            case "Base10": {
                // １時間30分 -> 1.５０
                const { hours, minutes } = this.toValues();
                return hours + NumberUtils.round(minutes / 60, 0.01);
            }
            case "Base60": {
                // １時間30分 -> 01:30
                return this.format({});
            }
        }
    }
    format({ suffixHours = ":", suffixMinutes = "", suffixSeconds = "", includeSecond = false, hoursZeroPad = true, minutesZeroPad = true, }) {
        const duration = this.toValues();
        const zeroPad = (num) => String(num).padStart(2, "0");
        const formattedHours = hoursZeroPad
            ? zeroPad(duration.hours)
            : duration.hours;
        const formattedMinutes = minutesZeroPad
            ? zeroPad(duration.minutes)
            : duration.minutes;
        const formatted = `${formattedHours}${suffixHours}${formattedMinutes}${suffixMinutes}`;
        const formattedSeconds = `${zeroPad(duration.seconds)}${suffixSeconds}`;
        return `${formatted}${includeSecond ? formattedSeconds : ""}`;
    }
}
//# sourceMappingURL=duration.js.map